import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import ApplicationLogo from '@/Components/Assets/ApplicationLogo'
import { Link } from '@/Components/Navigation/Link'
import { classNames } from '@/Utils/Utils'
import { ChangeClientUrl, Navigation } from '@/types'
import { User } from '@/types/models'
import { BottomNavigationList, NavigationList } from '@/Layouts/Partials/SidebarNavigation/NavigationList'
import { ClientNavigationSection } from '@/Layouts/Partials/SidebarNavigation/ClientNavigationSection'
import { UserMenuBottom } from '@/Layouts/Partials/UserMenu'

export default function MobileSidebar({
    sidebarOpen,
    setSidebarOpen,
    navigation,
    user,
    changeClientUrl,
}: {
    sidebarOpen: boolean
    setSidebarOpen: (open: boolean) => void
    navigation: Navigation
    user: User
    changeClientUrl?: ChangeClientUrl
}) {
    return (
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
            <DialogBackdrop transition className="fixed inset-0 bg-stone-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0" />

            <div className="fixed inset-0 flex">
                <DialogPanel
                    transition
                    className="relative flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
                >
                    <TransitionChild>
                        <div className="absolute top-0 left-full flex w-10 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                            <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                <span className="sr-only">Close sidebar</span>
                                <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                            </button>
                        </div>
                    </TransitionChild>

                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-stone-200 px-6 pt-2 pb-4">
                        <div className="-mx-3 mt-2 flex h-12 shrink-0 items-center">
                            <Link href={route('dashboard')}>
                                <ApplicationLogo className="w-full" />
                            </Link>
                        </div>

                        <nav className="mt-2 flex flex-1 flex-col" aria-label="Sidebar">
                            <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                <li className="grow">
                                    <div className="relative -mx-2 h-full">
                                        <ul
                                            role="list"
                                            className={classNames(
                                                'flex w-full flex-col space-y-1 transition-all duration-300 ease-in-out',
                                                navigation.displayClientNavigation
                                                    ? 'pointer-events-none absolute inset-0 -translate-x-full opacity-0'
                                                    : 'relative translate-x-0 opacity-100'
                                            )}
                                        >
                                            <NavigationList items={navigation.sections.global} />
                                        </ul>

                                        <ul
                                            role="list"
                                            className={classNames(
                                                'flex w-full flex-col space-y-1 transition-all duration-300 ease-in-out',
                                                navigation.displayClientNavigation
                                                    ? 'relative translate-x-0 opacity-100'
                                                    : 'pointer-events-none absolute inset-0 translate-x-full opacity-0'
                                            )}
                                        >
                                            {navigation.displayClientNavigation && (
                                                <ClientNavigationSection navigation={navigation} changeClientUrl={changeClientUrl!} />
                                            )}
                                        </ul>
                                    </div>
                                </li>

                                <li className="mt-auto">
                                    <ul role="list" className="-mx-2 mb-5 space-y-0.5">
                                        <BottomNavigationList items={navigation.sections.bottom} />
                                    </ul>

                                    <UserMenuBottom user={user} userNavigation={navigation.sections.user} />
                                </li>
                            </ul>
                        </nav>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
